import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { LogoIcon } from '@/components/LogoIcon';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  Menu, ArrowLeft, Download,
  Sparkles, BookOpen, Scale, CreditCard, Info,
} from 'lucide-react';

const navLinks = [
  { href: '/features', label: 'Features', icon: Sparkles },
  { href: '/how-to-use', label: 'How to Use', icon: BookOpen },
  { href: '/compare', label: 'Compare', icon: Scale },
  { href: '/pricing', label: 'Pricing', icon: CreditCard },
  { href: '/about', label: 'About', icon: Info },
];

export function MarketingHeader() {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const closeSheet = () => setIsOpen(false);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 pt-safe-area transition-[background-color,border-color,box-shadow] duration-200",
          isScrolled
            ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm"
            : "bg-background/98 border-b border-border/60"
        )}
        role="banner"
      >
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center justify-between" aria-label="Main navigation">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <LogoIcon size="md" className="rounded-xl" />
            <span className="font-display text-xl font-bold text-foreground">Leka</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.href} to={link.href}>
                <Button
                  variant="ghost"
                  size="sm"
                  className={isActive(link.href) ? 'bg-primary/10 text-primary' : ''}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>

          {/* Desktop Auth Actions */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <Link to="/translate">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to App
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="ghost" size="sm">Sign In</Button>
                </Link>
                <Link to="/auth?signup=true">
                  <Button variant="hero" size="sm">Get Started</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px]">
                <SheetHeader>
                  <SheetTitle className="text-left">Navigation</SheetTitle>
                </SheetHeader>

                <nav className="flex flex-col gap-1 mt-6">
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={closeSheet}
                        className={cn(
                          "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                          isActive(link.href)
                            ? 'bg-primary/10 text-primary'
                            : 'text-foreground hover:bg-muted'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        {link.label}
                      </Link>
                    );
                  })}

                  <Link
                    to="/download"
                    onClick={closeSheet}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive('/download')
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted'
                    )}
                  >
                    <Download className="w-4 h-4" />
                    Get the App
                  </Link>
                </nav>

                {/* Auth Section */}
                <div className="mt-6 pt-6 border-t border-border">
                  {user ? (
                    <Link to="/translate" onClick={closeSheet}>
                      <Button variant="hero" className="w-full">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to App
                      </Button>
                    </Link>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Link to="/auth" onClick={closeSheet}>
                        <Button variant="ghost" className="w-full justify-start">
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/auth?signup=true" onClick={closeSheet}>
                        <Button variant="hero" className="w-full">
                          Get Started
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
          </nav>
        </div>
      </header>
      <div aria-hidden="true" className="h-[73px] md:h-[69px]" />
    </>
  );
}
